import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { FaPlay, FaStop, FaPause } from "react-icons/fa";
import { GoogleMap, Polyline } from "@react-google-maps/api";
import { useGoogleMaps } from "components/GoogleMapsProvider/GoogleMapsProvider";
import { AiOutlineReload } from "react-icons/ai";
import "./custom-vehicle.css";
import { utils } from "helper";

const mapContainerStyle = {
  width: "100%",
  height: "calc(100% - 133px)",
};

const center = { lat: 19.0, lng: 75.7 };

const calculateBearing = (start, end) => {
  if (!start || !end) return 0;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;

  const dLon = toRad(end.lng - start.lng);
  const lat1 = toRad(start.lat);
  const lat2 = toRad(end.lat);

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  const brng = Math.atan2(y, x);
  return (toDeg(brng) + 360) % 360;
};

const NewRunningVehicleGps = ({ timeline }) => {
  const [skipIdleStopped, setSkipIdleStopped] = useState(false);
  const isLoaded = useGoogleMaps();
  const [playing, setPlaying] = useState(false);
  const [speedMultiplier, setSpeedMultiplier] = useState(50);
  const [currentIdx, setCurrentIdx] = useState(0);
  const animationRef = useRef(null);
  const mapRef = useRef(null);
  const [interpolatedPosition, setInterpolatedPosition] = useState(null);
  const truckRef = useRef(null);

  const onUnloaded = useCallback(() => {
    cancelAnimationFrame(animationRef.current);
    setPlaying(false);
    setCurrentIdx(0);
  }, []);

  const fullRoute = useMemo(() => {
    if (!timeline?.length) return [];

    return timeline.flatMap((item) =>
      item.path.map((pt) => ({
        lat: +pt.lat,
        lng: +pt.lon,
        dttime: utils.epochFormatDate(new Date(pt?.epoch * 1000)),
        speed: +pt.speed,
        status: item.status,
      }))
    );
  }, [timeline]);

  const route = useMemo(() => {
    if (!timeline?.length) return [];

    if (skipIdleStopped) {
      return timeline.flatMap((item) =>
        item.status === "Running"
          ? item.path.map((pt) => ({
              lat: +pt.lat,
              lng: +pt.lon,
              dttime: utils.epochFormatDate(new Date(pt?.epoch * 1000)),
              speed: +pt.speed,
              status: item.status,
            }))
          : []
      );
    } else {
      return fullRoute;
    }
  }, [timeline, skipIdleStopped, fullRoute]);

  const runningIndices = useMemo(() => {
    return fullRoute
      .map((pt, idx) => (pt.status === "Running" ? idx : null))
      .filter((idx) => idx !== null);
  }, [fullRoute]);

  const getFullRouteIndex = useCallback(
    (filteredIndex) => {
      if (!skipIdleStopped) return filteredIndex;

      if (filteredIndex >= 0 && filteredIndex < route.length) {
        const point = route[filteredIndex];
        return fullRoute.findIndex(
          (p) =>
            p.lat === point.lat &&
            p.lng === point.lng &&
            p.dttime === point.dttime
        );
      }
      return 0;
    },
    [skipIdleStopped, route, fullRoute]
  );

  const getFilteredRouteIndex = useCallback((fullIndex) => {
    if (!skipIdleStopped){
      return Math.min(fullIndex, fullRoute.length - 1);
    } 
    
    if (fullIndex >= 0 && fullIndex < fullRoute.length) {
      const point = fullRoute[fullIndex];
      const foundIndex = route.findIndex(p => 
        p.lat === point.lat && 
        p.lng === point.lng && 
        p.dttime === point.dttime
      );
      return foundIndex >= 0 ? foundIndex : 0;
    }
    return 0;
  }, [skipIdleStopped, route, fullRoute]);

  useEffect(() => {
    if (!playing || currentIdx >= route?.length - 1) return;
  
    const start = route[currentIdx];
    const end = route[currentIdx + 1];
    
    if (!start || !end) return;
    
    const duration = 5000 / speedMultiplier;
    let startTime = null;
  
    const animateFrame = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / duration;
  
      if (progress < 1) {
        const lat = (start?.lat || 0) + ((end?.lat || 0) - (start?.lat || 0)) * progress;
        const lng = (start?.lng || 0) + ((end?.lng || 0) - (start?.lng || 0)) * progress;
        const pos = { lat, lng };
        setInterpolatedPosition(pos);
  
        if (mapRef.current) mapRef.current.panTo(pos);
        animationRef.current = requestAnimationFrame(animateFrame);
      } else {
        setCurrentIdx((prev) => prev + 1);
        setInterpolatedPosition(end);
      }
    };
  
    animationRef.current = requestAnimationFrame(animateFrame);
    return () => cancelAnimationFrame(animationRef.current);
  }, [playing, currentIdx, speedMultiplier, route]);

  useEffect(() => {
    if (mapRef.current && interpolatedPosition && window.google) {
      const overlay = new window.google.maps.OverlayView();

      overlay.onAdd = function () {
        const div = document.createElement("div");
        div.className = "custom-truck-marker";
        truckRef.current = div;

        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(div);
      };

      overlay.draw = function () {
        if (!truckRef.current) return;

        const projection = this.getProjection();
        const position = projection.fromLatLngToDivPixel(
          new window.google.maps.LatLng(
            interpolatedPosition.lat,
            interpolatedPosition.lng
          )
        );
        const bearing = calculateBearing(
          route[currentIdx - 1] || route[0],
          interpolatedPosition
        );

        truckRef.current.style.left = `${position.x}px`;
        truckRef.current.style.top = `${position.y}px`;
        truckRef.current.style.transform = `translate(-50%, -50%) rotate(${bearing}deg)`;
      };

      overlay.onRemove = function () {
        if (truckRef.current && truckRef.current.parentNode) {
          truckRef.current.parentNode.removeChild(truckRef.current);
          truckRef.current = null;
        }
      };

      overlay.setMap(mapRef.current);

      return () => {
        overlay.setMap(null);
      };
    }
  }, [interpolatedPosition, currentIdx, route]);

  const ignitionGradient = useMemo(() => {
    if (!timeline?.length) return "";

    const allPaths = timeline.flatMap((item) => {
      return item.path.map((pt) => ({
        status: item.status,
      }));
    });

    const segmentWidth = 100 / allPaths.length;

    return allPaths
      .map((pt, i) => {
        let color;
        if (pt.status === "Running") {
          color = "#00c853";
        } else if (pt.status === "Idle") {
          color = "#FFAC05";
        } else if (pt.status === "Stopped") {
          color = "#d50000";
        } else {
          color = "#d50000";
        }
        return `${color} ${i * segmentWidth}%, ${color} ${
          (i + 1) * segmentWidth
        }%`;
      })
      .join(", ");
  }, [timeline]);

  if (!isLoaded) return <div>Loading map...</div>;

  const currentPosition = interpolatedPosition || route[currentIdx];
  const fullRouteIndex = getFullRouteIndex(currentIdx);

  const handleSeek = (e) => {
    const idx = Number(e.target.value);
    const filteredIndex = getFilteredRouteIndex(idx);

    if (filteredIndex >= 0 && filteredIndex < route.length) {
      setCurrentIdx(filteredIndex);
      setInterpolatedPosition(route[filteredIndex]);
    }
  };

  return (
    <div className="__gpsMapbx">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={12}
        center={currentPosition || center}
        onUnmount={onUnloaded}
        onLoad={(map) => (mapRef.current = map)}
      >
        <Polyline
          path={route.slice(0, currentIdx + 1)}
          options={{
            strokeColor: "#0f52ff",
            strokeOpacity: 0.9,
            strokeWeight: 4,
            zIndex: 2,
          }}
        />
        <Polyline
          path={route.slice(currentIdx)}
          options={{
            strokeColor: "#aaaaaa",
            strokeOpacity: 0.5,
            strokeWeight: 2,
            zIndex: 1,
          }}
        />
      </GoogleMap>

      <div
        className="__dummyPlayer_seekbar"
        style={{ margin: "10px 0", width: "100%" }}
      >
        <input
          type="range"
          min="0"
          max={fullRoute.length - 1}
          value={fullRouteIndex}
          onChange={handleSeek}
          style={{
            width: "100%",
            height: "8px",
            borderRadius: "4px",
            background: `linear-gradient(to right, ${ignitionGradient})`,
            appearance: "none",
            outline: "none",
          }}
        />
      </div>

      <div className="__dummyPlayer">
        <div className="__dummyPlayer_date">
          <React.Fragment>
            <input
              className="form-check-input"
              id="skip_check"
              type="checkbox"
              checked={skipIdleStopped}
              onChange={() => {
                setSkipIdleStopped((prev) => !prev);
                setCurrentIdx(0);
                setInterpolatedPosition(route[0]);
              }}
            />
            <label htmlFor="skip_check" className="form-check-label">
              Skip Idle/Stopped
            </label>
          </React.Fragment>
        </div>

        <div className="__dummyPlayer_date">
          <h4>{route[currentIdx]?.dttime || "--:--, --/--/----"}</h4>
          <h4>
            {" "}
            {route[currentIdx]?.speed !== undefined &&
            route[currentIdx]?.speed !== null
              ? `${route[currentIdx]?.speed} km/hr`
              : "-"}
          </h4>
        </div>

        <div className="__dummyPlayer_pl_stop">
          <button onClick={() => setPlaying(!playing)}>
            {playing ? <FaPause /> : <FaPlay />}
          </button>
          <button onClick={() => setPlaying(false)}>
            <FaStop />
          </button>
          <button
            onClick={() => {
              setCurrentIdx(0);
              setInterpolatedPosition(route[0]);
              setPlaying(true);
            }}
          >
            <AiOutlineReload />
          </button>
        </div>

        <div className="__dummyPlayer_speed">
          <p>Play Speed</p>
          {[10, 20, 50, 100, 200].map((sp) => (
            <button
              key={sp}
              className={speedMultiplier === sp ? "active" : ""}
              onClick={() => setSpeedMultiplier(sp)}
            >
              {sp}X
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewRunningVehicleGps;
